import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import { API_BASE } from "../../../ApiUrl";
import "./TenantIntegration.css";
import SettingsTabs from "../Settings/SettingsTabs";

const SECRET_REFERENCE = "tenant-integration-secret";

const emptyIntegration = {
  integration_type: "",
  scopes: [],
};

const TenantIntegration = () => {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tenantId, setTenantId] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState([
    {
      ...emptyIntegration,
    },
  ]);

  const [scopeInputs, setScopeInputs] = useState([""]);

  /* ==========================================================
     GET TENANT ID
  ========================================================== */

  useEffect(() => {
    const storedTenantUser =
      localStorage.getItem("tenant_user");

    if (!storedTenantUser) {
      Swal.fire({
        icon: "error",
        title: "Tenant information missing",
        text: "Please login again.",
      });

      return;
    }

    try {
      const tenantUser =
        JSON.parse(storedTenantUser);

      if (!tenantUser.tenant_id) {
        Swal.fire({
          icon: "error",
          title: "Tenant ID missing",
          text: "Tenant information could not be found.",
        });

        return;
      }

      setTenantId(
        Number(tenantUser.tenant_id)
      );
    } catch (error) {
      console.error(
        "Invalid tenant_user:",
        error
      );

      Swal.fire({
        icon: "error",
        title: "Invalid tenant session",
        text: "Please login again.",
      });
    }
  }, []);

  /* ==========================================================
     FETCH INTEGRATIONS
  ========================================================== */

  useEffect(() => {
    if (tenantId) {
      fetchIntegrations();
    }
  }, [tenantId]);

  const fetchIntegrations = async () => {
    try {
      setLoading(true);

      const response = await axios.post(
        `${API_BASE}/tenant-records/`,
        {
          tenant_id: Number(tenantId),
          tables: ["tenant_integrations"],
        }
      );

      console.log(
        "Tenant integrations response:",
        response.data
      );

      let data = response.data;

      if (response.data?.data) {
        data = response.data.data;
      }

      if (data?.tenant_integrations) {
        data = data.tenant_integrations;
      }

      if (!Array.isArray(data)) {
        data = [];
      }

      setIntegrations(data);
    } catch (error) {
      console.error(
        "Error fetching tenant integrations:",
        error
      );

      Swal.fire({
        icon: "error",
        title: "Unable to load integrations",
        text:
          error.response?.data?.detail ||
          error.response?.data?.message ||
          "Something went wrong while loading integrations.",
      });
    } finally {
      setLoading(false);
    }
  };

  /* ==========================================================
     HANDLE FORM CHANGE
  ========================================================== */

  const handleChange = (
    index,
    e
  ) => {
    const { name, value } = e.target;

    setFormData((prev) =>
      prev.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              [name]: value,
            }
          : item
      )
    );
  };

  /* ==========================================================
     ADD ANOTHER INTEGRATION
  ========================================================== */

  const addAnotherIntegration = () => {
    setFormData((prev) => [
      ...prev,
      {
        ...emptyIntegration,
      },
    ]);

    setScopeInputs((prev) => [
      ...prev,
      "",
    ]);
  };

  /* ==========================================================
     REMOVE INTEGRATION FORM
  ========================================================== */

  const removeIntegrationForm = (
    index
  ) => {
    if (formData.length === 1) {
      return;
    }

    setFormData((prev) =>
      prev.filter(
        (_, itemIndex) =>
          itemIndex !== index
      )
    );

    setScopeInputs((prev) =>
      prev.filter(
        (_, itemIndex) =>
          itemIndex !== index
      )
    );
  };

  /* ==========================================================
     ADD SCOPE
  ========================================================== */

  const addScope = (index) => {
    const scope =
      scopeInputs[index]?.trim();

    if (!scope) {
      return;
    }

    const alreadyExists =
      formData[index].scopes.includes(
        scope
      );

    if (alreadyExists) {
      setScopeInputs((prev) =>
        prev.map((item, itemIndex) =>
          itemIndex === index
            ? ""
            : item
        )
      );

      return;
    }

    setFormData((prev) =>
      prev.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              scopes: [
                ...item.scopes,
                scope,
              ],
            }
          : item
      )
    );

    setScopeInputs((prev) =>
      prev.map((item, itemIndex) =>
        itemIndex === index
          ? ""
          : item
      )
    );
  };

  /* ==========================================================
     REMOVE SCOPE
  ========================================================== */

  const removeScope = (
    integrationIndex,
    scopeToRemove
  ) => {
    setFormData((prev) =>
      prev.map((item, itemIndex) =>
        itemIndex ===
        integrationIndex
          ? {
              ...item,
              scopes:
                item.scopes.filter(
                  (scope) =>
                    scope !==
                    scopeToRemove
                ),
            }
          : item
      )
    );
  };

  /* ==========================================================
     SCOPE KEY DOWN
  ========================================================== */

  const handleScopeKeyDown = (
    index,
    e
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addScope(index);
    }
  };

  /* ==========================================================
     ADD INTEGRATION
  ========================================================== */

  const handleAdd = () => {
    setFormData([
      {
        ...emptyIntegration,
      },
    ]);

    setScopeInputs([""]);

    setIsEditing(false);
    setEditingId(null);
    setShowForm(true);
  };

  /* ==========================================================
     EDIT INTEGRATION
  ========================================================== */

  const handleEdit = (
    integration
  ) => {
    let scopes =
      integration.scopes || [];

    if (!Array.isArray(scopes)) {
      if (
        typeof scopes === "string"
      ) {
        try {
          const parsedScopes =
            JSON.parse(scopes);

          scopes = Array.isArray(
            parsedScopes
          )
            ? parsedScopes
            : [];
        } catch (error) {
          scopes = [];
        }
      } else {
        scopes = [];
      }
    }

    setFormData([
      {
        integration_type:
          integration.integration_type ||
          "",

        scopes,
      },
    ]);

    setScopeInputs([""]);

    setEditingId(
      integration.id
    );

    setIsEditing(true);
    setShowForm(true);
  };

  /* ==========================================================
     DELETE INTEGRATION
  ========================================================== */

  const handleDelete = async (
    integration
  ) => {
    const result =
      await Swal.fire({
        icon: "warning",
        title:
          "Delete Integration?",
        text: `Are you sure you want to delete "${
          integration.integration_type ||
          "this integration"
        }"?`,
        showCancelButton: true,
        confirmButtonText:
          "Yes, Delete",
        cancelButtonText:
          "Cancel",
        confirmButtonColor:
          "#dc2626",
      });

    if (!result.isConfirmed) {
      return;
    }

    try {
      setLoading(true);

      await axios.delete(
        `${API_BASE}/tenant-integrations/${integration.id}/`
      );

      await Swal.fire({
        icon: "success",
        title: "Deleted",
        text:
          "Integration deleted successfully.",
        timer: 1500,
        showConfirmButton: false,
      });

      await fetchIntegrations();
    } catch (error) {
      console.error(
        "Error deleting integration:",
        error
      );

      Swal.fire({
        icon: "error",
        title: "Delete failed",
        text:
          error.response?.data?.detail ||
          error.response?.data?.message ||
          "Unable to delete the integration.",
      });
    } finally {
      setLoading(false);
    }
  };

  /* ==========================================================
     VALIDATE FORM
  ========================================================== */

  const validateForm = () => {
    for (
      let index = 0;
      index < formData.length;
      index++
    ) {
      const integration =
        formData[index];

      if (
        !integration.integration_type.trim()
      ) {
        Swal.fire({
          icon: "warning",
          title:
            "Integration Type Required",
          text: `Please enter the integration type for Integration ${
            index + 1
          }.`,
        });

        return false;
      }
    }

    return true;
  };

  /* ==========================================================
     SAVE ALL INTEGRATIONS
  ========================================================== */

  const handleSubmit = async (
    e
  ) => {
    e.preventDefault();

    if (!tenantId) {
      Swal.fire({
        icon: "error",
        title: "Tenant ID missing",
        text:
          "Unable to identify the current tenant.",
      });

      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      /* ======================================================
         EDIT EXISTING INTEGRATION
      ====================================================== */

      if (isEditing) {
        const integration =
          formData[0];

        const payload = {
          integration_type:
            integration.integration_type.trim(),

          secret_reference:
            SECRET_REFERENCE,

          scopes:
            integration.scopes,

          tenant:
            Number(tenantId),
        };

        console.log(
          "Update integration payload:",
          payload
        );

        await axios.put(
          `${API_BASE}/tenant-integrations/${editingId}/`,
          payload
        );

        await Swal.fire({
          icon: "success",
          title:
            "Integration Updated",
          text:
            "Integration updated successfully.",
          timer: 1500,
          showConfirmButton: false,
        });
      }

      /* ======================================================
         ADD MULTIPLE INTEGRATIONS
      ====================================================== */

      else {
        const requests =
          formData.map(
            (integration) => {
              const payload = {
                integration_type:
                  integration.integration_type.trim(),

                secret_reference:
                  SECRET_REFERENCE,

                scopes:
                  integration.scopes,

                tenant:
                  Number(tenantId),
              };

              console.log(
                "Create integration payload:",
                payload
              );

              return axios.post(
                `${API_BASE}/tenant-integrations/`,
                payload
              );
            }
          );

        await Promise.all(
          requests
        );

        await Swal.fire({
          icon: "success",
          title:
            "Integrations Saved",
          text: `${formData.length} integration${
            formData.length > 1
              ? "s"
              : ""
          } saved successfully.`,
          timer: 1800,
          showConfirmButton: false,
        });
      }

      setShowForm(false);
      setIsEditing(false);
      setEditingId(null);

      setFormData([
        {
          ...emptyIntegration,
        },
      ]);

      setScopeInputs([""]);

      await fetchIntegrations();
    } catch (error) {
      console.error(
        "Error saving integrations:",
        error
      );

      const backendError =
        error.response?.data
          ?.detail ||
        error.response?.data
          ?.message ||
        error.response?.data ||
        "Unable to save integration.";

      Swal.fire({
        icon: "error",
        title: isEditing
          ? "Update failed"
          : "Save failed",
        text:
          typeof backendError ===
          "string"
            ? backendError
            : JSON.stringify(
                backendError
              ),
      });
    } finally {
      setLoading(false);
    }
  };

  /* ==========================================================
     CANCEL
  ========================================================== */

  const handleCancel = () => {
    setShowForm(false);
    setIsEditing(false);
    setEditingId(null);

    setFormData([
      {
        ...emptyIntegration,
      },
    ]);

    setScopeInputs([""]);
  };

  /* ==========================================================
     RENDER
  ========================================================== */

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="tenant-integration-content">
          <SettingsTabs />

          {!showForm && (
            <>
              <div className="tenant-integration-page-header">
                <div>
                  <h1>
                    Integrations
                  </h1>

                  <p>
                    Manage integrations
                    configured for your
                    organisation.
                  </p>
                </div>

                <button
                  type="button"
                  className="tenant-integration-add-btn"
                  onClick={handleAdd}
                >
                  + Add Integration
                </button>
              </div>

              <div className="tenant-integration-list-card">
                {loading ? (
                  <div className="tenant-integration-loading">
                    Loading integrations...
                  </div>
                ) : integrations.length ===
                  0 ? (
                  <div className="tenant-integration-empty">
                    <div className="tenant-integration-empty-icon">
                      ⚙
                    </div>

                    <h3>
                      No Integrations
                      Found
                    </h3>

                    <p>
                      Add the
                      integrations used
                      by your
                      organisation.
                    </p>

                    <button
                      type="button"
                      className="tenant-integration-empty-add-btn"
                      onClick={
                        handleAdd
                      }
                    >
                      + Add Integration
                    </button>
                  </div>
                ) : (
                  <div className="tenant-integration-table-wrapper">
                    <table className="tenant-integration-table">
                      <thead>
                        <tr>
                          <th>
                            Integration
                          </th>

                          <th>
                            Secret
                            Reference
                          </th>

                          <th>
                            Scopes
                          </th>

                          <th>
                            Actions
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {integrations.map(
                          (
                            integration
                          ) => {
                            let scopes =
                              integration.scopes ||
                              [];

                            if (
                              !Array.isArray(
                                scopes
                              )
                            ) {
                              if (
                                typeof scopes ===
                                "string"
                              ) {
                                try {
                                  const parsed =
                                    JSON.parse(
                                      scopes
                                    );

                                  scopes =
                                    Array.isArray(
                                      parsed
                                    )
                                      ? parsed
                                      : [];
                                } catch (
                                  error
                                ) {
                                  scopes =
                                    [];
                                }
                              } else {
                                scopes =
                                  [];
                              }
                            }

                            return (
                              <tr
                                key={
                                  integration.id
                                }
                              >
                                <td>
                                  <div className="tenant-integration-type-cell">
                                    <div className="tenant-integration-avatar">
                                      ⚙
                                    </div>

                                    <div>
                                      <strong>
                                        {integration.integration_type ||
                                          "-"}
                                      </strong>

                                      <span>
                                        Integration
                                      </span>
                                    </div>
                                  </div>
                                </td>

                                <td>
                                  <span className="tenant-integration-secret-reference">
                                    {integration.secret_reference ||
                                      SECRET_REFERENCE}
                                  </span>
                                </td>

                                <td>
                                  {scopes.length >
                                  0 ? (
                                    <div className="tenant-integration-table-scopes">
                                      {scopes.map(
                                        (
                                          scope
                                        ) => (
                                          <span
                                            key={
                                              scope
                                            }
                                          >
                                            {
                                              scope
                                            }
                                          </span>
                                        )
                                      )}
                                    </div>
                                  ) : (
                                    <span className="tenant-integration-no-scope">
                                      No scopes
                                    </span>
                                  )}
                                </td>

                                <td>
                                  <div className="tenant-integration-row-actions">
                                    <button
                                      type="button"
                                      className="tenant-integration-edit-btn"
                                      onClick={() =>
                                        handleEdit(
                                          integration
                                        )
                                      }
                                    >
                                      Edit
                                    </button>

                                    <button
                                      type="button"
                                      className="tenant-integration-delete-btn"
                                      onClick={() =>
                                        handleDelete(
                                          integration
                                        )
                                      }
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          }
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}

          {showForm && (
            <>
              <div className="tenant-integration-page-header">
                <div>
                  <h1>
                    {isEditing
                      ? "Edit Integration"
                      : "Add Integrations"}
                  </h1>

                  <p>
                    Add one or more
                    integrations for your
                    organisation.
                  </p>
                </div>

                <button
                  type="button"
                  className="tenant-integration-back-btn"
                  onClick={
                    handleCancel
                  }
                >
                  ← Back
                </button>
              </div>

              <form
                className="tenant-integration-card"
                onSubmit={
                  handleSubmit
                }
              >
                <div className="tenant-integration-card-body">
                  {formData.map(
                    (
                      integration,
                      index
                    ) => (
                      <div
                        className="tenant-integration-repeat-form"
                        key={index}
                      >
                        <div className="tenant-integration-form-header">
                          <div>
                            <h3>
                              Integration{" "}
                              {index + 1}
                            </h3>

                            <p>
                              Enter the
                              integration
                              details below.
                            </p>
                          </div>

                          {formData.length >
                            1 && (
                            <button
                              type="button"
                              className="tenant-integration-remove-form-btn"
                              onClick={() =>
                                removeIntegrationForm(
                                  index
                                )
                              }
                            >
                              Remove
                            </button>
                          )}
                        </div>

                        <div className="tenant-integration-grid">
                          <div className="tenant-integration-field">
                            <label>
                              Integration
                              Type{" "}
                              <span>
                                *
                              </span>
                            </label>

                            <input
                              type="text"
                              name="integration_type"
                              value={
                                integration.integration_type
                              }
                              onChange={(
                                e
                              ) =>
                                handleChange(
                                  index,
                                  e
                                )
                              }
                              placeholder="Enter integration type"
                              required
                            />
                          </div>
                        </div>

                        <div className="tenant-integration-scopes-section">
                          <label className="tenant-integration-scopes-label">
                            Access Scopes
                          </label>

                          <div className="tenant-integration-scope-input-row">
                            <input
                              type="text"
                              value={
                                scopeInputs[
                                  index
                                ] ||
                                ""
                              }
                              onChange={(
                                e
                              ) =>
                                setScopeInputs(
                                  (
                                    prev
                                  ) =>
                                    prev.map(
                                      (
                                        item,
                                        itemIndex
                                      ) =>
                                        itemIndex ===
                                        index
                                          ? e
                                              .target
                                              .value
                                          : item
                                    )
                                )
                              }
                              onKeyDown={(
                                e
                              ) =>
                                handleScopeKeyDown(
                                  index,
                                  e
                                )
                              }
                              placeholder="Enter scope, e.g. users.read"
                            />

                            <button
                              type="button"
                              className="tenant-integration-add-scope-btn"
                              onClick={() =>
                                addScope(
                                  index
                                )
                              }
                            >
                              Add Scope
                            </button>
                          </div>

                          {integration.scopes
                            .length >
                            0 && (
                            <div className="tenant-integration-scope-list">
                              {integration.scopes.map(
                                (
                                  scope
                                ) => (
                                  <div
                                    key={
                                      scope
                                    }
                                    className="tenant-integration-scope-tag"
                                  >
                                    <span>
                                      {
                                        scope
                                      }
                                    </span>

                                    <button
                                      type="button"
                                      onClick={() =>
                                        removeScope(
                                          index,
                                          scope
                                        )
                                      }
                                      aria-label={`Remove ${scope}`}
                                    >
                                      ×
                                    </button>
                                  </div>
                                )
                              )}
                            </div>
                          )}

                          {integration.scopes
                            .length ===
                            0 && (
                            <div className="tenant-integration-empty-scope">
                              No access
                              scopes added.
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  )}

                  {!isEditing && (
                    <button
                      type="button"
                      className="tenant-integration-add-another-btn"
                      onClick={
                        addAnotherIntegration
                      }
                    >
                      + Add Another
                      Integration
                    </button>
                  )}
                </div>

                <div className="tenant-integration-actions">
                  <button
                    type="button"
                    className="tenant-integration-cancel-btn"
                    onClick={
                      handleCancel
                    }
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="tenant-integration-save-btn"
                    disabled={loading}
                  >
                    {loading
                      ? "Saving..."
                      : isEditing
                      ? "Update Integration"
                      : `Save ${
                          formData.length
                        } Integration${
                          formData.length >
                          1
                            ? "s"
                            : ""
                        }`}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TenantIntegration;